import { useContext, useEffect, useState } from "react";
import { Link } from "react-router";
import { AppContext } from "../AppProvider";
import Card from "./ui/Card";

const AssigmentFilter = () => {
  const { assignments } = useContext(AppContext);
  const [subServiceAreas, setSubServiceAreas] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedSubService, setSelectedSubService] = useState("");
  const [selectedPracticeArea, setSelectedPracticeArea] = useState("");
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Extract unique sub-service and practice areas
  useEffect(() => {
    if (assignments.length > 0) {
      const subServices = [
        ...new Set(
          assignments.map((item) => item.sub_service_area?.name).filter(Boolean)
        ),
      ];

      const allPracticeAreas = assignments.flatMap(
        (item) => item.practice_areas_visible_for || []
      );

      const practiceAreaNames = [
        ...new Set(allPracticeAreas.map((pa) => pa.name).filter(Boolean)),
      ];

      setSubServiceAreas(subServices);
      setPracticeAreas(practiceAreaNames);
    }
  }, [assignments]);

  // Filter logic
  useEffect(() => {
    if (assignments.length > 0) {
      const filtered = assignments.filter((item) => {
        const matchesService =
          !selectedService || item?.service_type === selectedService;

        const matchesSubService =
          !selectedSubService ||
          item.sub_service_area?.name === selectedSubService;

        const matchesPracticeArea =
          !selectedPracticeArea ||
          (item.practice_areas_visible_for &&
            item.practice_areas_visible_for.some(
              (pa) => pa.name === selectedPracticeArea
            ));

        return matchesService && matchesSubService && matchesPracticeArea;
      });

      setFilteredAssignments(filtered);
      setLoading(false);
    }
  }, [assignments, selectedService, selectedSubService, selectedPracticeArea]);

  const isFiltered =
    selectedService || selectedSubService || selectedPracticeArea;

  return (
    <div className="z-20 w-full max-w-7xl mx-auto px-2 sm:px-4">
      <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 md:gap-6 p-2 sm:p-4 bg-gray-50 rounded-lg">
        {/* Service Dropdown */}
        <div className="w-full sm:w-auto min-w-[150px] relative">
          <select
            className="appearance-none border border-gray-300 p-1.5 sm:p-2 rounded-md text-sm sm:text-base w-full bg-white cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none pr-8"
            onChange={(ev) => setSelectedService(ev.target.value)}
          >
            <option value="" className="text-gray-500">Service</option>
            <option value="OD" className="py-1">Organization Development</option>
            <option value="RE" className="py-1">Research and Evaluation</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>

        {/* Sub-Service Dropdown */}
        {subServiceAreas.length > 0 && (
          <div className="w-full sm:w-auto min-w-[150px] relative">
            <select
              className="appearance-none border border-gray-300 p-1.5 sm:p-2 rounded-md text-sm sm:text-base w-full bg-white cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none pr-8"
              onChange={(ev) => setSelectedSubService(ev.target.value)}
            >
              <option value="" className="text-gray-500">Sub-Service</option>
              {subServiceAreas.map((name, index) => (
                <option key={`subservice-${name}-${index}`} value={name} className="py-1">
                  {name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        )}

        {/* Practice Area Dropdown */}
        {practiceAreas.length > 0 && (
          <div className="w-full sm:w-auto min-w-[150px] relative">
            <select
              className="appearance-none border border-gray-300 p-1.5 sm:p-2 rounded-md text-sm sm:text-base w-full bg-white cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none pr-8"
              onChange={(ev) => setSelectedPracticeArea(ev.target.value)}
            >
              <option value="" className="text-gray-500">Sectoral presence</option>
              {practiceAreas.map((name, index) => (
                <option key={`practice-${name}-${index}`} value={name} className="py-1">
                  {name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="text-center py-4">
          <p className="text-gray-600">Loading assignments...</p>
        </div>
      )}

      {/* Render Filtered Assignments */}
      {isFiltered && !loading && (
        <div className="mt-4 sm:mt-6  flex flex-col justify-center items-center">
          {filteredAssignments?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 ">
              {filteredAssignments.map((assignment, index) => (
                <Link
                  key={`assignment-${assignment.id || `temp-${index}`}`}
                  to={`/assignments/${assignment.id}`}
                  className="w-full h-full transition-transform hover:scale-[1.02]"
                >
                  <Card
                    title={assignment?.title}
                    description={assignment?.description}
                    id={assignment?.id}
                    photo={assignment?.photo}
                    logo={assignment?.company_logo}
                  />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No assignments found.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssigmentFilter;
